import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText,
  Button,
  TextField,
  Paper,
  Typography
} from '@mui/material';
import Surface from '../3D/models/Surface.jpg';
import { BASE_URL } from '../../urlConfig/Url';

const BuildingVisualization = () => {
  const mountRef = useRef(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [assetBarcode, setAssetBarcode] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [hoverDetails, setHoverDetails] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const roomMeshes = useRef(new Map()); // Store room meshes

  const handleAssignAsset = async () => {
    try {
      const response = await fetch(BASE_URL+'/api/dash/insertAssetBarcodeAssignedLoaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          AssetBarcode: assetBarcode,
          AssignedLocation: `B${selectedRoom.buildingId}-F${selectedRoom.floor}-R${selectedRoom.roomX}-${selectedRoom.roomZ}`
        })
      });
  
      if (response.ok) {
        resetAllRoomColors(); // Reset all rooms first
        setAlertMessage('Asset successfully assigned to room');
        setShowAlert(true);
        setAssetBarcode('');
        updateRoomColor(selectedRoom, ROOM_COLORS.ASSIGNED);
      } else {
        setAlertMessage('Failed to assign asset to room');
        setShowAlert(true);
      }
    } catch (error) {
      setAlertMessage('Error assigning asset to room');
      setShowAlert(true);
    }
  };

  const handleFindAsset = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/dash/findAssetLocation?AssetBarcode=${assetBarcode}`);
      const data = await response.json();
      
      if (response.ok && data.response?.length > 0 && data.response[0].AssignedLocation) {
        resetAllRoomColors(); // Reset all rooms first
        
        const location = data.response[0].AssignedLocation;
        const parts = location.split('-');
        const buildingPart = parts[0];
        const floorPart = parts[1];
        const roomPart = parts[2];
        const zonePart = parts[3];
  
        const buildingId = parseInt(buildingPart.substring(1));
        const floor = parseInt(floorPart.substring(1));
        const roomX = parseInt(roomPart.substring(1));
        const roomZ = parseInt(zonePart);
        
        const foundRoom = {
          buildingId,
          floor,
          roomX,
          roomZ
        };
  
        setSelectedRoom(foundRoom);
        updateRoomColor(foundRoom, ROOM_COLORS.FOUND);
      } else {
        setAlertMessage('Asset not found');
        setShowAlert(true);
      }
    } catch (error) {
      setAlertMessage('Error finding asset');
      setShowAlert(true);
    }
  };

  const fetchRoomDetails = async (roomData) => {
    try {
      const response = await fetch(`${BASE_URL}/api/dash/getRoomDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location: `B${roomData.buildingId}-F${roomData.floor}-R${roomData.roomX}-${roomData.roomZ}`
        })
      });
      
      if (response.ok) {
        const details = await response.json();
        setHoverDetails(details);
      }
    } catch (error) {
      console.error('Error fetching room details:', error);
    }
  };

  // Add these constants at the top of your component, outside useEffect
const ROOM_COLORS = {
  DEFAULT: 0xffffff,  // White/default color
  SELECTED: 0x0000ff, // Blue for selected
  FOUND: 0xff0000,    // Red for found asset
  ASSIGNED: 0x00ff00  // Green for assigned
};

// Add this function inside your component, before useEffect
const resetAllRoomColors = () => {
  roomMeshes.current.forEach((mesh) => {
    if (mesh.material) {
      mesh.material.opacity = 0.0;  // Keep transparency
      mesh.material.color.set(ROOM_COLORS.DEFAULT);
    }
  });
};

  const updateRoomColor = (roomData, color) => {
    const key = `B${roomData.buildingId}-F${roomData.floor}-R${roomData.roomX}-${roomData.roomZ}`;
    const roomMesh = roomMeshes.current.get(key);
    if (roomMesh) {
      roomMesh.material.color.set(color);
      roomMesh.material.opacity = 0.3; // Make it slightly visible
    }
  };

  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x87ceeb);
    mountRef.current.appendChild(renderer.domElement);

    // Function to create a tree with proper ground placement
    const createTree = (position) => {
      const treeGroup = new THREE.Group();
      
      // Create tree trunk - Adjusted height and position
      const trunkGeometry = new THREE.CylinderGeometry(0.2, 0.4, 2, 8);
      const trunkMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x4a2821,
        // Add slight roughness for better visual
        roughness: 0.7
      });
      const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
      // Move trunk up by half its height to sit on ground
      trunk.position.y = 1;
      
      // Create tree leaves with adjusted positions
      const leavesGeometry = new THREE.ConeGeometry(1.5, 3, 8);
      const leavesMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x0d5302,
        // Add slight roughness for better visual
        roughness: 0.6 
      });
      
      const leaves1 = new THREE.Mesh(leavesGeometry, leavesMaterial);
      leaves1.position.y = 3; // Adjusted to sit above trunk
      
      const leaves2 = new THREE.Mesh(leavesGeometry, leavesMaterial);
      leaves2.position.y = 4;
      leaves2.scale.set(0.8, 0.8, 0.8);
      
      const leaves3 = new THREE.Mesh(leavesGeometry, leavesMaterial);
      leaves3.position.y = 5;
      leaves3.scale.set(0.6, 0.6, 0.6);
      
      // Add all parts to the tree group
      treeGroup.add(trunk);
      treeGroup.add(leaves1);
      treeGroup.add(leaves2);
      treeGroup.add(leaves3);
      
      // Add a small margin from the surface
      const surfaceMargin = 0.1; // Adjust this value for different heights from surface
      position.y += surfaceMargin;
      
      // Position the tree
      treeGroup.position.copy(position);
      
      // Add a slight random rotation for variety
      treeGroup.rotation.y = Math.random() * Math.PI * 2;
      
      return treeGroup;
    };

    // Add trees around buildings with proper margins from buildings
    const treePositions = [
      new THREE.Vector3(-12, 0, -25),
      new THREE.Vector3(-45, 0, 0),
      new THREE.Vector3(-25, 0, 35),
      new THREE.Vector3(12, 0, -25),
      new THREE.Vector3(27, 0, 0),
      new THREE.Vector3(45, 0, 35),
      new THREE.Vector3(0, 0, 45),
      new THREE.Vector3(-10, 0, 15),
      new THREE.Vector3(20, 0, -40)
    ];

    // Function to check if a position is too close to buildings
    const isPositionValid = (position) => {
      // Define minimum distance from buildings
      const minBuildingDistance = 5;
      
      // Building positions (center points)
      const buildingPositions = [
        new THREE.Vector3(-35, 0, -20), // Building 1
        new THREE.Vector3(-35, 0, 20),  // Building 2
        new THREE.Vector3(0, 0, -10),   // Building 3
        new THREE.Vector3(35, 0, 20),   // Building 4
        new THREE.Vector3(35, 0, -20)   // Building 5
      ];
      
      // Check distance from each building
      for (const buildingPos of buildingPositions) {
        const distance = position.distanceTo(buildingPos);
        if (distance < minBuildingDistance) {
          return false;
        }
      }
      
      return true;
    }

    // Add trees with validation
    treePositions.forEach(position => {
      if (isPositionValid(position)) {
        const tree = createTree(position);
        scene.add(tree);
      }
    });

    // Raycaster for room selection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Main floor
    const floorGeometry = new THREE.PlaneGeometry(100, 100);
    const textureLoader = new THREE.TextureLoader();
    const surfaceTexture = textureLoader.load(Surface);
    const floorMaterial = new THREE.MeshBasicMaterial({ map: surfaceTexture });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    scene.add(floor);

    // Function to create a sprite with text
    const createTextSprite = (text, position) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      context.font = 'Bold 50px Arial';
      context.fillStyle = 'rgba(0, 0, 0, 1.0)';
      context.fillText(text, 0, 40);
      
      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(10, 5, 1); // Adjust the scale as needed
      sprite.position.copy(position);
      return sprite;
    };

    // Function to create building frame with clickable rooms
    const createBuildingFrame = (width, depth, floorHeight, numFloors, roomsX, roomsZ, position, buildingId) => {
      const buildingGroup = new THREE.Group();
      
      // Create floors
      for (let floor = 0; floor < numFloors; floor++) {
        // Create room frames for each floor
        for (let x = 0; x < roomsX; x++) {
          for (let z = 0; z < roomsZ; z++) {
            // Create visible frame
            const frameGeometry = new THREE.EdgesGeometry(
              new THREE.BoxGeometry(width/roomsX, floorHeight, depth/roomsZ)
            );
            const frameMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
            const frame = new THREE.LineSegments(frameGeometry, frameMaterial);
            
            // Create invisible clickable mesh
            const roomGeometry = new THREE.BoxGeometry(width/roomsX, floorHeight, depth/roomsZ);
            const roomMaterial = new THREE.MeshBasicMaterial({ 
              transparent: true, 
              opacity: 0.0 
            });
            const room = new THREE.Mesh(roomGeometry, roomMaterial);
            
            // Set position for both frame and room
            const roomPosition = new THREE.Vector3(
              (x - (roomsX-1)/2) * (width/roomsX),
              floor * floorHeight + floorHeight/2,
              (z - (roomsZ-1)/2) * (depth/roomsZ)
            );
            
            frame.position.copy(roomPosition);
            room.position.copy(roomPosition);
            
            // Add metadata to room
            room.userData = {
              buildingId,
              floor: floor + 1,
              roomX: x + 1,
              roomZ: z + 1
            };

            // Store room mesh by its identifier
            const key = `B${buildingId}-F${floor+1}-R${x+1}-${z+1}`;
            roomMeshes.current.set(key, room);
            
            buildingGroup.add(frame);
            buildingGroup.add(room);
          }
        }
      }

      // Add building number sprite
      const sprite = createTextSprite(`Building ${buildingId}`, new THREE.Vector3(0, numFloors * floorHeight + 2, 0));
      buildingGroup.add(sprite);

      buildingGroup.position.copy(position);
      scene.add(buildingGroup);
    };

    // Create all buildings
    createBuildingFrame(15, 15, 4, 2, 3, 3, new THREE.Vector3(-35, 0, -20), 1);
    createBuildingFrame(12, 12, 4, 4, 2, 2, new THREE.Vector3(-35, 0, 20), 2);
    createBuildingFrame(25, 10, 4, 1, 5, 2, new THREE.Vector3(0, 0, -10), 3);
    createBuildingFrame(15, 10, 4, 1, 3, 2, new THREE.Vector3(35, 0, 20), 4);
    // Add new building
    createBuildingFrame(15, 15, 4, 5, 3, 3, new THREE.Vector3(35, 0, -20), 5);

    // Lighting setup
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 10, 5);
    scene.add(directionalLight);

    // Camera setup
    camera.position.set(-50, 50, -50);
    camera.lookAt(0, 0, 0);

    // Orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // Double click handler
    const handleDoubleClick = (event) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);
      const clickedRoom = intersects.find(intersect => 
        intersect.object.userData && 
        intersect.object.userData.buildingId !== undefined
      );
    
      if (clickedRoom) {
        resetAllRoomColors(); // Reset all rooms first
        const roomData = clickedRoom.object.userData;
        const selectedRoomData = {
          buildingId: roomData.buildingId,
          floor: roomData.floor,
          roomX: roomData.roomX,
          roomZ: roomData.roomZ
        };
        setSelectedRoom(selectedRoomData);
        setAssetBarcode('');
        updateRoomColor(selectedRoomData, ROOM_COLORS.SELECTED);
      }
    };

    // Add hover handler
    const handleMouseMove = (event) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);
      const hoveredRoom = intersects.find(intersect => 
        intersect.object.userData && 
        intersect.object.userData.buildingId !== undefined
      );

      if (hoveredRoom) {
        // Clear any existing timeouts
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);

        // Set new timeout for showing details
        hoverTimeoutRef.current = setTimeout(() => {
          fetchRoomDetails(hoveredRoom.object.userData);
        }, 800);
      } else {
        // Clear show timeout if exists
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        
        // Set timeout to hide details
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
          setHoverDetails(null);
        }, 5000);
      }
    };

    // Add event listener
    renderer.domElement.addEventListener('dblclick', handleDoubleClick);
    renderer.domElement.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    /// Cleanup
    return () => {
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
        renderer.domElement.removeEventListener('dblclick', handleDoubleClick);
        renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      }
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      
      {/* Asset Search Panel */}
      <Paper 
        elevation={3} 
        sx={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          padding: 2,
          width: '300px'
        }}
      >
        <Typography variant="h6" gutterBottom>Find Asset</Typography>
        <TextField
          fullWidth
          size="small"
          value={assetBarcode}
          onChange={(e) => setAssetBarcode(e.target.value)}
          placeholder="Enter asset barcode"
          margin="normal"
        />
        <Button 
          variant="contained" 
          onClick={handleFindAsset}
          fullWidth
          sx={{ mt: 1 }}
        >
          Find
        </Button>
      </Paper>

      {/* Selected Room Panel */}
      {selectedRoom && (
        <Paper 
          elevation={3}
          sx={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: 2,
            width: '300px'
          }}
        >
          <Typography variant="h6" gutterBottom>Room Details</Typography>
          <Typography>Building: {selectedRoom.buildingId}</Typography>
          <Typography>Floor: {selectedRoom.floor}</Typography>
          <Typography>Room: {selectedRoom.roomX}-{selectedRoom.roomZ}</Typography>
          
          <TextField
            fullWidth
            size="small"
            value={assetBarcode}
            onChange={(e) => setAssetBarcode(e.target.value)}
            placeholder="Enter asset barcode"
            margin="normal"
          />
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAssignAsset}
            fullWidth
            sx={{ mt: 1, mb: 1 }}
          >
            Assign
          </Button>
          <Button 
            variant="outlined"
            onClick={() => setSelectedRoom(null)}
            fullWidth
          >
            Close
          </Button>
        </Paper>
      )}

      {/* Hover Details */}
      {hoverDetails && hoverDetails.response.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            padding: 2,
            maxWidth: '300px',
            maxHeight: '400px',  
            overflowY: 'auto'
          }}
        >
          <Typography variant="h6" gutterBottom>Room Information</Typography>
          {hoverDetails.response.map(item => (
            <div key={item.ID} style={{ marginBottom: '16px' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                {/* ID: {item.ID}{"\n"} */}
                Barcode: {item.Barcode}{"\n"}
                Description: {item.Description} {"\t"}
                Category: {item.Category}{"\n"}
                Assigned Location: {item.AssignedLocation}
              </pre>
            </div>
          ))}
        </Paper>
      )}

      {/* Alert Dialog */}
      <Dialog
        open={showAlert}
        onClose={() => setShowAlert(false)}
      >
        <DialogTitle>Notification</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {alertMessage}
          </DialogContentText>
        </DialogContent>
        <Button 
          onClick={() => setShowAlert(false)}
          sx={{ m: 2 }}
          variant="contained"
        >
          OK
        </Button>
      </Dialog>
    </div>
  );
};

export default BuildingVisualization;